import React from 'react';
import { useTranslation } from 'react-i18next';
import NumberFloatingFilter from 'ui/AgGridReact/NumberFloatingFilter';
import { BudgetExpenseOrIncome, BudgetSections } from 'business/constants';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import {
    trackEventBudget,
    TRACKABLE_EVENTS,
    ACTIONS,
} from 'helpers/trackerHelpers';
import { useGetBudgetUserExerciceSettings } from 'routes/BudgetModule/components/shared/BudgetReportToggle/hooks';
import {
    BUDGET_BALANCE_PREFIX,
    getExcelFilename,
} from 'routes/BudgetModule/contexts/helpers';
import AgGridReactExposingApi from 'routes/BudgetModule/components/shared/AgGridReactExposingAPI';
import {
    getIndicatorsData,
    getAmountsByChapters,
    getMetricsForFinalIndicator,
    getFinalIndicatorColumnField,
    getFinalIndicatorFormatted,
    getFinalIndicatorWithReportFormatted,
    getHighlightedMetric,
    IndicatorFilterRow,
} from './helpers';
import { getColumns } from './gridHelpers';
import TooltipCellRenderer from './TooltipCellRenderer';
import { useGetBalanceData } from './hooks';
import BudgetBalanceTableFooter from './BudgetBalanceTableFooter';
import BudgetBalanceTableHeader from './BudgetBalanceTableHeader';
import { StyAccordion, StyGrid } from './styles';
import { Skeleton } from '@mui/material';
import {
    BUDGET_EXCEL_EXPORT_PARAMS,
    BUDGET_EXCEL_STYLES,
} from 'routes/BudgetModule/config/excelExportHelpers';
import { INDICATORS_FILTERS } from './constants';

type Props = {
    title: string;
    budgetProposals: BudgetProposalT[];
    referenceYear: number;
    expenseOrIncome: BudgetExpenseOrIncome;
    budgetSection: BudgetSections;
    budgetName: string | null | undefined;
    additionalIndicators: string[];
    finalIndicator: string;
};

export default function BudgetBalanceTable({
    title,
    budgetProposals,
    referenceYear,
    expenseOrIncome,
    budgetSection,
    budgetName,
    additionalIndicators,
    finalIndicator,
}: Props): React.ReactElement {
    const { t } = useTranslation();

    const { data: balanceData = [], isFetching } = useGetBalanceData(
        referenceYear,
        expenseOrIncome,
        budgetSection,
        budgetName,
    );

    const { budgetReportExerciceData } =
        useGetBudgetUserExerciceSettings(referenceYear);

    const metrics = getMetricsForFinalIndicator(
        budgetProposals.filter(
            (bp) =>
                bp.section === budgetSection && bp.exercice === referenceYear,
        ),
    );

    const { isReportedAmountUsed } = budgetReportExerciceData;

    const finalIndicatorColumnField = getFinalIndicatorColumnField(metrics);

    const colDefs = getColumns(
        referenceYear,
        t,
        isReportedAmountUsed,
        finalIndicatorColumnField,
    );

    const groupedByChapterData = isFetching
        ? []
        : getAmountsByChapters(balanceData);

    const indicatorTotalsData = isFetching
        ? []
        : getIndicatorsData(balanceData, additionalIndicators);

    const finalAmountFormatted = isFetching
        ? undefined
        : getFinalIndicatorFormatted(balanceData, metrics);

    const finalAmountWithReportFormatted = isFetching
        ? undefined
        : getFinalIndicatorWithReportFormatted(balanceData, metrics);

    const trackOnToggle = (event: React.SyntheticEvent, expanded: boolean) => {
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetOverview.totals.expandBlock,
            {
                block: t(title),
                action: expanded ? ACTIONS.open : ACTIONS.close,
            },
        );
    };

    const highlightedMetric = getHighlightedMetric(
        finalIndicatorColumnField,
        isReportedAmountUsed,
    );

    const sortedRows: ResponseDataT[] = [];
    additionalIndicators
        // to avoid duplication of rows just before the subtotal
        .slice(0, -1)
        .forEach((indicator: string, index: number) => {
            const filteredData = groupedByChapterData.filter(
                (row: IndicatorFilterRow) =>
                    INDICATORS_FILTERS[indicator](row, true),
            );

            sortedRows.push(...filteredData);
            sortedRows.push(indicatorTotalsData[index]);
        });
    sortedRows.push(indicatorTotalsData[indicatorTotalsData.length - 1]);
    // to keep chapter 001 and 002
    const indicatorMissingRow =
        additionalIndicators[additionalIndicators.length - 1];
    const missingRows = groupedByChapterData.filter(
        (row: IndicatorFilterRow) =>
            !INDICATORS_FILTERS[indicatorMissingRow](row, true),
    );
    sortedRows.push(...missingRows);

    return (
        <StyAccordion onChange={trackOnToggle}>
            <BudgetBalanceTableHeader
                title={title}
                finalAmountFormatted={
                    isReportedAmountUsed
                        ? finalAmountWithReportFormatted
                        : finalAmountFormatted
                }
                isFetching={isFetching}
            />
            <StyGrid
                id={`budget-balance-table-${budgetSection}-${expenseOrIncome}`}
                className="ag-theme-material"
                highlightedMetric={highlightedMetric}
            >
                {isFetching ? (
                    <Skeleton variant="rectangular" sx={{ height: 700 }} />
                ) : (
                    <div>
                        <AgGridReactExposingApi
                            rowData={sortedRows}
                            columnDefs={colDefs}
                            rowHeight={36}
                            headerHeight={40}
                            defaultColDef={{
                                flex: 1,
                                minWidth: 80,
                            }}
                            components={{
                                numberFloatingFilter: NumberFloatingFilter,
                                tooltipCellRenderer: TooltipCellRenderer,
                            }}
                            domLayout="autoHeight"
                            excelExport={{
                                fileName: getExcelFilename(
                                    BUDGET_BALANCE_PREFIX,
                                ),
                                sheetName: t(title),
                            }}
                            excelStyles={BUDGET_EXCEL_STYLES}
                            defaultExcelExportParams={
                                BUDGET_EXCEL_EXPORT_PARAMS
                            }
                        />
                        <BudgetBalanceTableFooter
                            finalIndicator={finalIndicator}
                            finalAmountFormatted={finalAmountFormatted}
                            finalAmountWithReportFormatted={
                                finalAmountWithReportFormatted
                            }
                            isReportedAmountUsed={isReportedAmountUsed}
                        />
                    </div>
                )}
            </StyGrid>
        </StyAccordion>
    );
}
